import { useState, useRef } from 'react';
import { CalendarIcon, Upload, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { getUploadSignedUrl, createSourceNode, stripUtf8Bom } from '@/services/pipeline';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

function dateToS3DirName(date) {
  if (!(date instanceof Date)) {
    throw new Error("Input must be a Date object");
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `year=${year}/month=${month}/day=${day}`;
}

function FileUploadWithDate({
  selectedFile,
  selectedDate,
  setSelectedFile,
  setSelectedDate,
  accept,
  maxSize,
  className,
  placeholder = "Select a file...",
  dateLabel = "Date",
  fileLabel = "File",
}) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (maxSize && file.size > maxSize) {
        alert(`File size exceeds ${Math.round(maxSize / 1024 / 1024)}MB limit`);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setIsCalendarOpen(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Date Selection */}
      <div className="space-y-2">
        <Label htmlFor="date">{dateLabel}</Label>
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              id="date"
              variant="outline"
              className={cn(
                'w-full justify-start text-left font-normal',
                !selectedDate && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDate ? (
                selectedDate.toDateString()
              ) : (
                <span>Pick a date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* File Upload */}
      <div className="space-y-2">
        <Label htmlFor="file">{fileLabel}</Label>
        <div className="relative">
          <Input
            id="file"
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="hidden"
          />
          <label htmlFor="file">
            <div className="flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 p-6 text-center hover:border-muted-foreground/50 transition-colors">
              <div className="space-y-2">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
                  <Upload className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {selectedFile ? selectedFile.name : placeholder}
                  </p>
                  {selectedFile ? (
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile.size)} • Click to change
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Click to browse or drag and drop
                    </p>
                  )}
                  {maxSize && (
                    <p className="text-xs text-muted-foreground">
                      Max size: {Math.round(maxSize / 1024 / 1024)}MB
                    </p>
                  )}
                </div>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Status */}
      {selectedFile && selectedDate && (
        <div className="rounded-lg bg-muted p-3">
          <div className="flex items-center space-x-2">
            <div className="h-2 w-2 rounded-full bg-green-500" />
            <p className="text-sm text-muted-foreground">
              Ready: {selectedFile.name} for {selectedDate.toDateString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function UploadFileWithDatePickerDialog({
  open,
  onOpenChange,
  dataSourceId,
  sourceName,
  projectId
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  // Default to today: the date is only the S3 partition label, and an empty
  // date used to leave Submit silently disabled (reported as "upload broken").
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedFile || !selectedDate) return;
    if (!sourceName) {
      // Without the source's name the ingest would mint a NEW source named
      // after the file — fail loudly instead.
      toast.error("Source not loaded yet — reopen the dialog and try again.");
      return;
    }
    setIsUploading(true);

    try {
      // 1. Sign a PUT under the project's own prefix (never the legacy
      //    global vdd/datasources/... path — files signed there are
      //    invisible to the source's file listing, which scans the
      //    per-project config.s3_prefix).
      const cleanFile = await stripUtf8Bom(selectedFile);
      const signed = await getUploadSignedUrl(cleanFile.name);

      // 2. PUT the bytes straight to S3.
      const uploadResponse = await fetch(signed.url, {
        method: "PUT",
        headers: { "Content-Type": "application/octet-stream" },
        body: cleanFile
      });
      if (!uploadResponse.ok) {
        toast.error("Upload failed. Please try again.");
        return;
      }

      // 3. Ingest into THIS source: the backend copies the object under
      //    data-governance/<slug>/datasources/<id>/<datePath>/, stamps
      //    config.s3_prefix, and kicks off schema analysis.
      await createSourceNode({
        kind: 'file',
        source_name: sourceName,
        s3_uri: signed.s3_uri,
        date: dateToS3DirName(selectedDate),
      });

      toast.success("File uploaded — analyzing schema…");
      setTimeout(() => {
        onOpenChange(false);
      }, 800);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error?.message || "Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const canSubmit = selectedFile && selectedDate;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Datasource</DialogTitle>
          </DialogHeader>

          <FileUploadWithDate
            setSelectedFile={setSelectedFile}
            setSelectedDate={setSelectedDate}
            selectedDate={selectedDate}
            selectedFile={selectedFile}
            accept=".csv,.xlsx,.xls,.parquet,.json,.ndjson"
          />

          {!canSubmit && (
            <p className="text-xs text-muted-foreground">
              {!selectedFile ? "Choose a file to upload." : "Pick a date for this upload."}
            </p>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Toaster />
    </>
  );
}
